import { format } from 'date-fns';
import { translate } from 'src/translate';
import Utils from './Utils';
import SentryLogger from 'src/components/logging/SentryLogger';

export default class ScheduleManager {
  constructor(data, language = null) {
    this.data = data;
    this.language = language;
  }

  getStaffList() {
    return Object.keys(this.data.staff)
      .map((staffMember) => ({
        id: staffMember,
        name: this.data?.availableOptions[staffMember]?.title[this.language],
        isHidden: this.data?.availableOptions[staffMember]?.isHidden === true ? true : false,
      }))
      .filter((staff) => !staff.isHidden);
  }

  getStaffServices(staffMember) {
    const staff = this.data.availableOptions[staffMember];

    let staffKeysSorted = Object.keys(staff?.aptOptions || {});
    staffKeysSorted = staffKeysSorted.sort(
      (a, b) => (staff?.aptOptions[a].rank ?? 1000) - (staff?.aptOptions[b].rank ?? 1000),
    );

    return staffKeysSorted
      .filter(
        (serviceId) =>
          !staff.aptOptions[serviceId].isHidden ||
          staff.aptOptions[serviceId].overrideHiddenInDashboard,
      )
      .map((serviceId) => {
        // Extract the title with fallback
        const title = staff?.aptOptions[serviceId]?.title[this.language]
          ? staff?.aptOptions[serviceId]?.title[this.language]
          : staff?.aptOptions[serviceId]?.title[
              Object.keys(staff?.aptOptions[serviceId]?.title)[0]
            ] || 'No title available';

        // Check and extract the description
        const descriptionObj = staff?.aptOptions[serviceId]?.description;
        let description = null; // Default to null if description is unavailable
        if (descriptionObj) {
          if (descriptionObj[this.language]) {
            description = descriptionObj[this.language];
          } else {
            description = descriptionObj[Object.keys(descriptionObj)[0]] || null;
          }
        }

        // description = 'Lorem Ipsum'

        return {
          id: serviceId,
          name: title,
          duration: staff?.aptOptions[serviceId]?.durationMins,
          description: description,
        };
      });
  }

  getServiceDetails(staffMember, serviceKey) {
    const services = this.getStaffServices(staffMember);
    return services ? services[serviceKey] : null;
  }

  getServiceTimeSlots(staffMember, serviceKey) {
    const staffData = this.data.staff?.[staffMember];
    if (!staffData || !staffData.apts) {
      const sentryLogger = new SentryLogger();
      sentryLogger.error(new Error('Missing staff data in getServiceTimeSlots'), {
        extra: {
          staffMember,
          serviceKey,
          staffExists: !!staffData,
          aptsExists: !!staffData?.apts,
          availableStaffKeys: Object.keys(this.data.staff || {}),
          context: 'schedule_manager_get_service_time_slots',
        },
      });
      return {};
    }

    const service = staffData.apts[serviceKey];
    let schedule = {};

    if (service && service.dates) {
      Object.entries(service.dates).forEach(([date, dateDetails]) => {
        const availableTimes = dateDetails.availableOptions
          ? Object.entries(dateDetails.availableOptions).map(([time, timeDetails]) => {
              const { DateTime } = require('luxon');
              const titleFromApi = Utils.getLanguageOrDefault(timeDetails, this.language);
              const title =
                titleFromApi ||
                DateTime.fromMillis(Number(time)).setZone('Asia/Jerusalem').toFormat('HH:mm');
              return { time, title };
            })
          : [];
        schedule[date] = availableTimes;
      });
    }
    return schedule;
  }

  getServiceDaysSlots(staffMember, serviceKey, language) {
    const availableSlots = this.getServiceTimeSlots(staffMember, serviceKey);
    return Object.keys(availableSlots).map((key) => ({
      key: key.toString(),
      date: new Date(parseInt(key)),
      dateFormatted: format(new Date(parseInt(key)), 'yyyy-MM-dd'),
      dayText:
        language === 'en'
          ? new Date(parseInt(key)).toLocaleDateString(language, { weekday: 'short' })
          : new Date(parseInt(key)).toLocaleDateString(language, { weekday: 'long' }),
    }));
  }

  static getChosenDateFormatted(chosenDate, language) {
    if (/^\d+$/.test(chosenDate)) {
      // If it's a valid number in string format, convert it to a number
      chosenDate = Number(chosenDate);
    }

    const date = new Date(chosenDate);

    const options = {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    };

    // Formatting the date to include Arabic day names but with English numbers
    const formattedDate = new Intl.DateTimeFormat(`en-IL`, options).format(date);
    return formattedDate;
  }

  static getCustomerAlreadyScheduledTxt(chosenDate, language) {
    // Formatting the date to include day names in English with specified format and time
    const formattedDate = Utils.formatMillis(chosenDate, language);

    // Return the message with the formatted date

    return (
      <span>
        <p>
          {translate('alreadyScheduledCustomerMessageP1', language)} <b>{formattedDate}</b>.
        </p>
        <p> {translate('alreadyScheduledCustomerMessageP2', language)}</p>
      </span>
    );
  }

  static getFooterText(data, staffMember, service, language) {
    if (data === null || staffMember === null || service === null) return '';

    const staffName = Utils.getLanguageOrDefault(data.availableOptions[staffMember], language);
    const serviceName = Utils.getLanguageOrDefault(
      data.availableOptions[staffMember]?.aptOptions[service],
      language,
    );

    return `${staffName} - ${serviceName}`;
  }

  static getChosenTimeFormatted(chosenTime) {
    // Convert the timestamp to a number and create a Date object
    if (/^\d+$/.test(chosenTime)) {
      // If it's a valid number in string format, convert it to a number
      chosenTime = Number(chosenTime);
    }

    const date = new Date(chosenTime);
    // Options for formatting the time
    const options = {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    };

    // Formatting the time
    const formattedTime = new Intl.DateTimeFormat('en-GB', options).format(date);
    return formattedTime;
  }

  static isCustomerScheduled(upcomingEvents, customers, customer) {
    const { DateTime } = require('luxon');

    const customerObj = customers.filter((e) => e.number === customer);

    if (customerObj.length === 0 || upcomingEvents === null) {
      return null;
    }

    const customerEvent = upcomingEvents.filter((event) => event.uid === customerObj[0].id);

    if (customerEvent.length === 0) {
      return null;
    }

    // Get current time in Asia/Jerusalem time zone
    const now = DateTime.now().setZone('Asia/Jerusalem');

    // Get event start time from ISO string
    const eventStartTime = DateTime.fromISO(customerEvent[0].startISO, { zone: 'Asia/Jerusalem' });

    // // Check if current time is after the event start time
    // if (now >= eventStartTime) {
    //   return null;
    // }

    const enrichedEvents = customerEvent.map((event) => {
      const startDate = DateTime.fromISO(event.startISO || event.start).setZone('Asia/Jerusalem');
      const endDate = DateTime.fromISO(event.endISO || event.end).setZone('Asia/Jerusalem');

      return {
        ...event,
        startFormatted: startDate.toFormat('HH:mm'),
        endFormatted: endDate.toFormat('HH:mm'),
      };
    });

    return enrichedEvents;
  }

  static addOOOHoursToEvents = (eventsByDay, oooHours, specificDatesExceptions) => {
    if (!oooHours) return eventsByDay;
    const { DateTime } = require('luxon');

    Object.keys(eventsByDay).forEach((date) => {
      const dayOfWeek = DateTime.fromISO(date).toFormat('cccc');
      const oooHoursForDay = oooHours[dayOfWeek];

      if (specificDatesExceptions[date]) {
        let specificOOOHours = specificDatesExceptions[date];
        // if (specificDatesExceptions[date].length === 0) {
        //   specificOOOHours.push({ start: '00:00', end: '23:59' })
        // } else {
        //   specificOOOHours = ScheduleManager.findOOOHours(specificDatesExceptions[date]);
        // }

        specificOOOHours.forEach((oooHour) => {
          // Convert start time to UTC ISO string
          const startDateTimeLocal = DateTime.fromISO(`${date}T${oooHour.start}`, {
            zone: 'Asia/Jerusalem',
          });
          const startISO = startDateTimeLocal.toUTC().toISO();

          const endDateTimeLocal = DateTime.fromISO(`${date}T${oooHour.end}`, {
            zone: 'Asia/Jerusalem',
          });
          const endISO = endDateTimeLocal.toUTC().toISO();

          const oooEvent = {
            start: { dateTime: oooHour.start },
            end: { dateTime: oooHour.end },
            startFormatted: oooHour.start,
            endFormatted: oooHour.end,
            startISO: startISO,
            endISO,
            aptBooking: {
              durationMins: Utils.diffInMinutes(startISO, endISO),
            },
            color: 'pink',
            summary: 'OOO',
          };
          // Add the OOO event to the day's events
          eventsByDay[date].push(oooEvent);
        });
      } else if (oooHoursForDay) {
        oooHoursForDay.forEach((oooHour) => {
          // Convert start time to UTC ISO string
          const startDateTimeLocal = DateTime.fromISO(`${date}T${oooHour.start}`, {
            zone: 'Asia/Jerusalem',
          });
          const startISO = startDateTimeLocal.toUTC().toISO();

          const endDateTimeLocal = DateTime.fromISO(`${date}T${oooHour.end}`, {
            zone: 'Asia/Jerusalem',
          });
          const endISO = endDateTimeLocal.toUTC().toISO();

          const oooEvent = {
            start: { dateTime: oooHour.start },
            end: { dateTime: oooHour.end },
            startFormatted: oooHour.start,
            endFormatted: oooHour.end,
            startISO: startISO,
            endISO,
            aptBooking: {
              durationMins: Utils.diffInMinutes(startISO, endISO),
            },
            color: 'pink',
            summary: 'OOO',
          };
          // Add the OOO event to the day's events
          eventsByDay[date].push(oooEvent);
        });
      }
    });

    return eventsByDay;
  };

  // given working hours e.g. {start: "9:00", end: "20:00"}
  // this functions finds the complementary, in this example:
  // [{start: "00:00", end: "09:00"}, {start: "20:00", end: "23:59"} ]
  static findOOOHours = (dailyWorkingHours) => {
    dailyWorkingHours.sort((a, b) => a.start.localeCompare(b.start));

    const oooHours = [];
    let prevEnd = '00:00';

    dailyWorkingHours.forEach((interval, index) => {
      if (prevEnd < interval.start) {
        oooHours.push({ start: prevEnd, end: interval.start });
      }
      prevEnd = interval.end;

      if (index === dailyWorkingHours.length - 1 && prevEnd < '23:59' && prevEnd !== '00:00') {
        oooHours.push({ start: prevEnd, end: '23:59' });
      }
    });

    if (dailyWorkingHours.length === 0) {
      oooHours.push({ start: '00:00', end: '23:59' });
    }

    return oooHours;
  };

  static aggregateEventsByDay = (flattenedEvents, staffCalendarInfo) => {
    const { DateTime } = require('luxon');

    let eventsByDay = {};

    // Aggregate events by day
    flattenedEvents.forEach((event) => {
      const eventDay = DateTime.fromISO(event.startISO).toFormat('yyyy-MM-dd');

      if (!eventsByDay[eventDay]) {
        eventsByDay[eventDay] = [];
      }

      eventsByDay[eventDay].push(event);
    });

    eventsByDay = ScheduleManager.addOOOHoursToEvents(
      eventsByDay,
      staffCalendarInfo?.oooHours,
      ScheduleManager.formatSpecificDates(staffCalendarInfo?.specificOOOHours),
    );

    // Sort events within each day
    Object.keys(eventsByDay).forEach((day) => {
      eventsByDay[day].sort((a, b) => {
        const timeA = DateTime.fromISO(a.startISO);
        const timeB = DateTime.fromISO(b.startISO);
        return timeA - timeB;
      });
    });

    // find first event for each day
    Object.keys(eventsByDay).forEach((day) => {
      const events = eventsByDay[day];
      for (let event of events) {
        if (event.eventId || event.aptTypeId) {
          // Assuming 'id' refers to 'eventId' or 'aptTypeId'
          event.isFirstEvent = true;
          break; // Stop after adding isFirstEvent to the first event with an id
        }
      }
    });

    return eventsByDay;
  };

  static eventHasStartTimestamp(event) {
    if (event == null || event.startTimestamp == null || event.startTimestamp === '') {
      return false;
    }
    return Number.isFinite(Number(event.startTimestamp));
  }

  static flattenEvents(events, type) {
    const flattenEventsHelper = (events, type) => {
      return Object.values(events).flatMap((userEvents) =>
        Object.values(userEvents).flatMap((event) => {
          if (!ScheduleManager.eventHasStartTimestamp(event)) {
            return [];
          }
          // Assuming each event is an object, we're adding or updating the 'type' property
          return { ...event, type: type };
        }),
      );
    };

    let flattenedEventsFuture = flattenEventsHelper(events, type);
    // let flattenedEventsPast = flattenEventsHelper(eventsPast);

    return flattenedEventsFuture;
  }

  static shouldShowCancellationReason(selectedCustomerBooked) {
    if (!selectedCustomerBooked) return false;
    const startTimestamp = Number(selectedCustomerBooked.startTimestamp);
    return startTimestamp > 0 && startTimestamp <= Date.now();
  }

  // converts from 14/05/2024 to 2024-05-14
  static formatSpecificDates(dateObject) {
    const newObject = {};

    for (const key in dateObject) {
      if (dateObject.hasOwnProperty(key)) {
        const [day, month, year] = key.split('/');
        const newKey = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
        newObject[newKey] = dateObject[key];
      }
    }

    return newObject;
  }

  /**
   * Splits multi-day break events into individual single-day events.
   * Each split event keeps the same eventId and gets metadata about the multi-day span.
   * @param {Array} breakEvents - Array of break event objects
   * @returns {Array} - Array of break events with multi-day events split into single days
   */
  static splitMultiDayBreakEvents(breakEvents) {
    const { DateTime } = require('luxon');
    const result = [];

    for (const event of breakEvents) {
      // Skip if not a break event or missing required timestamps
      if (!event.start || !event.end) {
        result.push(event);
        continue;
      }

      // Parse start and end times in Asia/Jerusalem timezone
      const startDT = DateTime.fromISO(event.start, { zone: 'Asia/Jerusalem' });
      const endDT = DateTime.fromISO(event.end, { zone: 'Asia/Jerusalem' });

      // Check if start and end are on different calendar days
      const startDate = startDT.startOf('day');
      const endDate = endDT.startOf('day');
      const daysDiff = Math.floor(endDate.diff(startDate, 'days').days);

      // If same day, pass through unchanged
      if (daysDiff === 0) {
        result.push(event);
        continue;
      }

      // Multi-day event - split into individual days
      const totalDays = daysDiff + 1;
      const originalStartISO = event.start;
      const originalEndISO = event.end;
      const originalStartTimestamp = event.startTimestamp;

      for (let dayIndex = 0; dayIndex < totalDays; dayIndex++) {
        const currentDay = startDate.plus({ days: dayIndex });
        
        let dayStart, dayEnd;
        
        if (dayIndex === 0) {
          // First day: original start time to 23:59
          dayStart = startDT;
          dayEnd = currentDay.set({ hour: 23, minute: 59, second: 59 });
        } else if (dayIndex === totalDays - 1) {
          // Last day: 00:00 to original end time
          dayStart = currentDay.set({ hour: 0, minute: 0, second: 0 });
          dayEnd = endDT;
        } else {
          // Middle days: 00:00 to 23:59
          dayStart = currentDay.set({ hour: 0, minute: 0, second: 0 });
          dayEnd = currentDay.set({ hour: 23, minute: 59, second: 59 });
        }

        // Create split event with all original properties
        const splitEvent = {
          ...event,
          start: dayStart.toISO(),
          end: dayEnd.toISO(),
          startTimestamp: dayStart.toMillis().toString(),
          // Mark as multi-day event with metadata
          isMultiDayEvent: true,
          multiDayEventInfo: {
            totalDays,
            dayIndex, // 0-indexed
            originalStart: originalStartISO,
            originalEnd: originalEndISO,
            originalStartTimestamp,
          },
        };

        result.push(splitEvent);
      }
    }

    return result;
  }

  static getCalendarWeeklyEvents(
    events,
    pastEvents,
    selectedStaff,
    staffCalendarInfo,
    selectedWeek,
    customEvents,
    getAptDetails = null,
  ) {
    const weeklyEvents = [];
    for (const day of selectedWeek) {
      let dayEvents = ScheduleManager.getCalendarEvents(
        events,
        pastEvents,
        selectedStaff,
        staffCalendarInfo,
        format(day.date, 'yyyy-MM-dd'),
        customEvents,
        getAptDetails,
      );

      dayEvents = dayEvents.map((event) => ({
        ...event,
        col: `col-start-${day.col}`,
        hoverBgColor: `hover:bg-${event.color}-50`,
        bgColor: `bg-${event.color}-100`,
        titleColor: `text-${event.color}-700`,
        hoverTimeColor: `text-${event.color}-700`,
      }));

      weeklyEvents.push(...dayEvents);
    }

    return weeklyEvents;
  }

  static getCalendarWeeklyEventsGrouped(
    events,
    pastEvents,
    selectedStaff,
    staffCalendarInfo,
    selectedWeek,
    customEvents,
    language,
    getAptDetails = null,
  ) {
    const weeklyEvents = [];
    for (const day of selectedWeek) {
      let dayEvents = ScheduleManager.getCalendarEventsGrouped(
        events,
        pastEvents,
        selectedStaff,
        staffCalendarInfo,
        format(day.date, 'yyyy-MM-dd'),
        customEvents,
        language,
        getAptDetails,
        2, // minGroupSize: always group events (2+)
      );

      dayEvents = dayEvents.map((event) => ({
        ...event,
        col: `col-start-${day.col}`,
        hoverBgColor: `hover:bg-${event.color}-50`,
        bgColor: `bg-${event.color}-100`,
        titleColor: `text-${event.color}-700`,
        hoverTimeColor: `text-${event.color}-700`,
      }));

      weeklyEvents.push(...dayEvents);
    }

    return weeklyEvents;
  }

  static getCalendarEvents(
    events,
    pastEvents,
    selectedStaff,
    staffCalendarInfo,
    selectedDay,
    customEvents,
    getAptDetails = null,
  ) {
    const { DateTime } = require('luxon');

    // let flattenedEvents = ScheduleManager.flattenEvents(events);

    let flattenedEvents = [...(events || []), ...(pastEvents || []), ...(customEvents || [])];

    if (!staffCalendarInfo) return [];

    flattenedEvents = flattenedEvents.filter((event) => event.staffId === selectedStaff);

    flattenedEvents.forEach((event) => {
      if (
        typeof event.startFormatted !== 'undefined' &&
        typeof event.endFormatted !== 'undefined'
      ) {
        return; // Skip to the next iteration
      }

      // Convert start and end times from ISO string to Israel time zone using Luxon
      const startDate = DateTime.fromISO(event.start).setZone('Asia/Jerusalem');
      const endDate = DateTime.fromISO(event.end).setZone('Asia/Jerusalem');

      // Format hours and minutes for startFormatted and endFormatted
      event.startFormatted = startDate.toFormat('HH:mm');
      event.endFormatted = endDate.toFormat('HH:mm');
      event.summary = event.eventTitle;
      event.startISO = event.start;
      event.endISO = event.end;

      event.start = { dateTime: event.startFormatted };
      event.end = { dateTime: event.endFormatted };
      event.description = '';

      // Get color from appointment service if available, otherwise default
      if (event.eventType === 'breaks') {
        event.color = 'gray';
      } else if (event.aptTypeId && getAptDetails) {
        const aptDetails = getAptDetails(event.aptTypeId);
        event.color = aptDetails?.color || 'blue';
      } else {
        event.color = 'blue';
      }
    });

    const aggregateEventsByDay = ScheduleManager.aggregateEventsByDay(
      flattenedEvents,
      staffCalendarInfo[selectedStaff],
    );

    // this is a patch in case a day doesnt have any event, we would still show the ooo hours events
    if (!aggregateEventsByDay[selectedDay]) {
      return ScheduleManager.addOOOHoursToEvents(
        { [selectedDay]: [] },
        staffCalendarInfo[selectedStaff]?.oooHours,
        ScheduleManager.formatSpecificDates(staffCalendarInfo[selectedStaff]?.specificOOOHours),
      )[selectedDay];
    }
    return aggregateEventsByDay[selectedDay];
  }

  static getCalendarEventsGrouped(
    events,
    pastEvents,
    selectedStaff,
    staffCalendarInfo,
    selectedDay,
    customEvents,
    language,
    getAptDetails = null,
    minGroupSize = 4,
  ) {
    const { DateTime } = require('luxon');

    let flattenedEvents = [...(events || []), ...(pastEvents || []), ...(customEvents || [])];

    if (!staffCalendarInfo) return [];

    flattenedEvents = flattenedEvents.filter((event) => event.staffId === selectedStaff);

    // Separate break events from regular events BEFORE formatting
    const breakEvents = flattenedEvents.filter(
      (event) => event.eventType === 'breaks' || event.summary === 'OOO'
    );
    const regularEvents = flattenedEvents.filter(
      (event) => event.eventType !== 'breaks' && event.summary !== 'OOO'
    );

    // Group regular events by startTimestamp
    const groupedByTimestamp = {};
    regularEvents.forEach((event) => {
      const groupKey = event.startTimestamp;
      if (!groupedByTimestamp[groupKey]) {
        groupedByTimestamp[groupKey] = [];
      }
      groupedByTimestamp[groupKey].push(event);
    });

    // Create group events where needed
    const processedRegularEvents = [];
    Object.values(groupedByTimestamp).forEach((eventsGroup) => {
      if (eventsGroup.length >= minGroupSize) {
        // For RTL languages (Arabic, Hebrew), use directional isolates to keep number LTR
        const isRTL = language === 'ar' || language === 'heb';
        const eventLabel = isRTL 
          ? `${translate('appointments', language)} \u2066${eventsGroup.length}\u2069`
          : `${eventsGroup.length} ${translate('appointments', language)}`;
        
        const groupEvent = {
          ...eventsGroup[0],
          eventTitle: eventLabel,
          summary: eventLabel,
          isGroupEvent: true,
          groupEvents: eventsGroup,
          arrivalConfirmation: eventsGroup.every((event) => event.arrivalConfirmation === true),
          isNoShow: eventsGroup.every((event) => event.isNoShow === true),
        };
        processedRegularEvents.push(groupEvent);
      } else {
        processedRegularEvents.push(...eventsGroup);
      }
    });

    // Combine processed regular events with break events
    const allProcessedEvents = [...processedRegularEvents, ...breakEvents];

    // Format all events
    allProcessedEvents.forEach((event) => {
      if (
        typeof event.startFormatted !== 'undefined' &&
        typeof event.endFormatted !== 'undefined'
      ) {
        return; // Skip to the next iteration
      }

      // Convert start and end times from ISO string to Israel time zone using Luxon
      const startDate = DateTime.fromISO(event.start).setZone('Asia/Jerusalem');
      const endDate = DateTime.fromISO(event.end).setZone('Asia/Jerusalem');

      // Format hours and minutes for startFormatted and endFormatted
      event.startFormatted = startDate.toFormat('HH:mm');
      event.endFormatted = endDate.toFormat('HH:mm');
      event.summary = event.summary || event.eventTitle;
      event.startISO = event.start;
      event.endISO = event.end;

      event.start = { dateTime: event.startFormatted };
      event.end = { dateTime: event.endFormatted };
      event.description = '';

      // Get color from appointment service if available, otherwise default
      if (event.eventType === 'breaks') {
        event.color = 'gray';
      } else if (event.aptTypeId && getAptDetails) {
        const aptDetails = getAptDetails(event.aptTypeId);
        event.color = aptDetails?.color || 'blue';
      } else {
        event.color = 'blue';
      }
    });

    const aggregateEventsByDay = ScheduleManager.aggregateEventsByDay(
      allProcessedEvents,
      staffCalendarInfo[selectedStaff],
    );

    // this is a patch in case a day doesnt have any event, we would still show the ooo hours events
    if (!aggregateEventsByDay[selectedDay]) {
      return ScheduleManager.addOOOHoursToEvents(
        { [selectedDay]: [] },
        staffCalendarInfo[selectedStaff]?.oooHours,
        ScheduleManager.formatSpecificDates(staffCalendarInfo[selectedStaff]?.specificOOOHours),
      )[selectedDay];
    }
    return aggregateEventsByDay[selectedDay];
  }

  static getCalendarAllEvents(
    events,
    pastEvents,
    staffList,
    staffCalendarInfo,
    customEvents,
    language,
    getAptDetails = null,
  ) {
    const { DateTime } = require('luxon');

    // Combine all events
    let flattenedEvents = [...(events || []), ...(pastEvents || []), ...(customEvents || [])];

    // Separate break events from regular events
    const breakEvents = flattenedEvents.filter(
      (event) => event.eventType === 'breaks' || event.color === 'gray' || event.color === 'pink'
    );
    const regularEvents = flattenedEvents.filter(
      (event) => event.eventType !== 'breaks' && event.color !== 'gray' && event.color !== 'pink'
    );

    // Group only regular events by startTimestamp AND staffId (exclude breaks)
    const groupedByTimestampAndStaff = {};
    regularEvents.forEach((event) => {
      // Create a composite key using both timestamp and staffId
      const groupKey = `${event.startTimestamp}_${event.staffId}`;

      if (!groupedByTimestampAndStaff[groupKey]) {
        groupedByTimestampAndStaff[groupKey] = [];
      }
      groupedByTimestampAndStaff[groupKey].push(event);
    });

    // Process groups and create combined events where needed
    const processedEvents = [];
    Object.values(groupedByTimestampAndStaff).forEach((eventsGroup) => {
      if (eventsGroup.length >= 4) {
        // Take the first event and add all events (including first) as groupEvents
        // For RTL languages (Arabic, Hebrew), use directional isolates to keep number LTR
        const isRTL = language === 'ar' || language === 'heb';
        const eventLabel = isRTL 
          ? `${translate('appointments', language)} \u2066${eventsGroup.length}\u2069`
          : `${eventsGroup.length} ${translate('appointments', language)}`;
        
        const groupEvent = {
          ...eventsGroup[0],
          eventTitle: eventLabel,
          summary: eventLabel,
          isGroupEvent: true,
          groupEvents: eventsGroup,
          arrivalConfirmation: eventsGroup.every((event) => event.arrivalConfirmation === true),
          isNoShow: eventsGroup.every((event) => event.isNoShow === true),
        };
        processedEvents.push(groupEvent);
      } else {
        // Single event or less than 4 events, add them individually
        processedEvents.push(...eventsGroup);
      }
    });

    // Add break events back (they are never grouped)
    processedEvents.push(...breakEvents);

    // Process each event to add formatted times
    processedEvents.forEach((event) => {
      if (
        typeof event.startFormatted !== 'undefined' &&
        typeof event.endFormatted !== 'undefined'
      ) {
        return;
      }

      const startDate = DateTime.fromISO(event.startISO || event.start).setZone('Asia/Jerusalem');
      const endDate = DateTime.fromISO(event.endISO || event.end).setZone('Asia/Jerusalem');

      event.startFormatted = startDate.toFormat('HH:mm');
      event.endFormatted = endDate.toFormat('HH:mm');
      event.summary = event.eventTitle;
      event.startISO = event.startISO || event.start;
      event.endISO = event.endISO || event.end;

      event.start = { dateTime: event.startFormatted };
      event.end = { dateTime: event.endFormatted };
      event.description = '';

      // Get color from appointment service if available, otherwise default
      if (event.eventType === 'breaks') {
        event.color = 'gray';
      } else if (event.aptTypeId && getAptDetails) {
        const aptDetails = getAptDetails(event.aptTypeId);
        event.color = aptDetails?.color || 'blue';
      } else {
        event.color = 'blue';
      }
    });

    // First group events by staff
    const eventsByStaff = {};
    staffList.forEach((staffId) => {
      eventsByStaff[staffId] = processedEvents.filter((event) => event.staffId === staffId);
    });

    // Process each staff's events with their own calendar info
    const result = {};

    // Process each staff's events
    staffList.forEach((staffId) => {
      const staffEvents = ScheduleManager.aggregateEventsByDay(
        eventsByStaff[staffId],
        staffCalendarInfo[staffId],
      );

      // Add to result object
      Object.keys(staffEvents).forEach((date) => {
        if (!result[date]) {
          result[date] = {};
          // Initialize all staff arrays for this date
          staffList.forEach((id) => {
            result[date][id] = [];
          });
        }
        result[date][staffId] = staffEvents[date];
      });
    });

    // Add today's date if it doesn't exist
    const today = DateTime.now().setZone('Asia/Jerusalem').toFormat('yyyy-MM-dd');
    if (!result[today]) {
      result[today] = {};
      staffList.forEach((staffId) => {
        result[today][staffId] = [];
      });
    }

    Object.keys(result).forEach((date) => {
      staffList.forEach((staffId) => {
        // Only process if the array is empty (no events)
        if (result[date][staffId].length === 0) {
          const staffCalInfo = staffCalendarInfo[staffId];
          if (!staffCalInfo) return;

          // Create a temporary object structure expected by addOOOHoursToEvents
          const tempEventsByDay = { [date]: [] };

          const processedEvents = ScheduleManager.addOOOHoursToEvents(
            tempEventsByDay,
            staffCalInfo.oooHours,
            ScheduleManager.formatSpecificDates(staffCalInfo.specificOOOHours),
          );

          // Update the result with processed events including OOO hours
          if (processedEvents[date]) {
            result[date][staffId] = processedEvents[date];
          }
        }
      });
    });

    return result;
  }
  static fillMissingDates(calendarObj, staffIds, staffCalendarInfo) {
    // Find min and max dates
    const dates = Object.keys(calendarObj);
    const sortedDates = dates.sort();
    const minDate = sortedDates[0];
    const maxDate = sortedDates[sortedDates.length - 1];

    // Create Date objects for iteration
    const startDate = new Date(minDate);
    const endDate = new Date(maxDate);
    const filledCalendar = { ...calendarObj };

    // Iterate through each date
    const currentDate = new Date(startDate);
    while (currentDate <= endDate) {
      const dateString = currentDate.toISOString().split('T')[0];

      // If date doesn't exist, create it with empty arrays for each staff
      if (!filledCalendar[dateString]) {
        filledCalendar[dateString] = {};
        staffIds.forEach((staffId) => {
          filledCalendar[dateString][staffId] = [];

          // Process OOO hours for new empty array
          const staffCalInfo = staffCalendarInfo[staffId];
          if (staffCalInfo) {
            const tempEventsByDay = { [dateString]: [] };
            const processedEvents = ScheduleManager.addOOOHoursToEvents(
              tempEventsByDay,
              staffCalInfo.oooHours,
              ScheduleManager.formatSpecificDates(staffCalInfo.specificOOOHours),
            );

            if (processedEvents[dateString]) {
              filledCalendar[dateString][staffId] = processedEvents[dateString];
            }
          }
        });
      } else {
        // If date exists but some staff are missing, add them with empty arrays
        staffIds.forEach((staffId) => {
          if (!filledCalendar[dateString][staffId]) {
            filledCalendar[dateString][staffId] = [];

            // Process OOO hours for new empty array
            const staffCalInfo = staffCalendarInfo[staffId];
            if (staffCalInfo) {
              const tempEventsByDay = { [dateString]: [] };
              const processedEvents = ScheduleManager.addOOOHoursToEvents(
                tempEventsByDay,
                staffCalInfo.oooHours,
                ScheduleManager.formatSpecificDates(staffCalInfo.specificOOOHours),
              );

              if (processedEvents[dateString]) {
                filledCalendar[dateString][staffId] = processedEvents[dateString];
              }
            }
          } else if (filledCalendar[dateString][staffId].length === 0) {
            // Process OOO hours for existing empty array
            const staffCalInfo = staffCalendarInfo[staffId];
            if (staffCalInfo) {
              const tempEventsByDay = { [dateString]: [] };
              const processedEvents = ScheduleManager.addOOOHoursToEvents(
                tempEventsByDay,
                staffCalInfo.oooHours,
                ScheduleManager.formatSpecificDates(staffCalInfo.specificOOOHours),
              );

              if (processedEvents[dateString]) {
                filledCalendar[dateString][staffId] = processedEvents[dateString];
              }
            }
          }
        });
      }

      // Move to next date
      currentDate.setDate(currentDate.getDate() + 1);
    }

    return filledCalendar;
  }
}
